/**
 * Freeze topology ↔ rechoose probability coupling (Track Z1 / C4).
 * Single pure helper used by controls + choreography so HUD and dynamics agree.
 *
 * Freeze is a *view of the graph*, not a permanent pin of rechoose at 0:
 *   freeze on  → rechooseProb = 0 (graph stops mind-changing)
 *   freeze off → if rechoose was 0, restore living default for this topology
 *   rechoose > 0 → clear freeze so the slider is not lying
 */

import { livingRechooseFor } from "./defaults";
import type { SimParams } from "./params";

/**
 * freeze true → rechooseProb = 0.
 * freeze false → leave rechoose if already living; if stuck at 0, restore
 * the topology-aware living default (so unfreeze is not a silent frozen graph).
 */
export function applyFreezeTopology(p: SimParams, freeze: boolean): SimParams {
  if (freeze) {
    return { ...p, freezeTopology: true, rechooseProb: 0 };
  }
  // Unfreeze
  if (p.rechooseProb > 0) {
    return { ...p, freezeTopology: false };
  }
  return {
    ...p,
    freezeTopology: false,
    rechooseProb: livingRechooseFor({ ...p, freezeTopology: false }),
  };
}

/**
 * Setting rechoose > 0 clears freeze so the user isn't stuck with a frozen
 * graph while the slider claims rechoose is active.
 * Setting rechoose to 0 does not auto-enable freeze (user may want p=0 without freeze flag).
 */
export function applyRechooseProb(p: SimParams, r: number): SimParams {
  const rechooseProb = Math.max(0, Math.min(1, r));
  if (rechooseProb > 0 && p.freezeTopology) {
    return { ...p, rechooseProb, freezeTopology: false };
  }
  return { ...p, rechooseProb };
}
